import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, InputAdornment,
    Table, TableBody, TableCell, TableHead, TableRow, Chip,
    IconButton, Tooltip, CircularProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Inventory as InventoryIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import axios from 'axios';
import AddProductModal from '../components/Inventory/AddProductModal';
import { API_URL } from '../config/api';

const InventoryPage = () => {
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
            const user = JSON.parse(userInfo).user || JSON.parse(userInfo);
            if (user.role === 'RECEPTIONIST') {
                navigate('/dashboard/receptionist');
            }
        }
    }, [navigate]);

    const getHeaders = useCallback(() => {
        const userInfo = localStorage.getItem('userInfo');
        const token = userInfo ? JSON.parse(userInfo).token : null;
        return { headers: { Authorization: `Bearer ${token}` } };
    }, []);


    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/inventory`, getHeaders());
            setProducts(response.data);
        } catch (error) {
            console.error('Error fetching inventory:', error);
        } finally {
            setLoading(false);
        }
    }, [getHeaders]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleEdit = (product: any) => {
        setSelectedProduct(product);
        setModalOpen(true);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                await axios.delete(`${API_URL}/inventory/${id}`, getHeaders());
                fetchProducts();
            } catch (error) {
                console.error('Error deleting product:', error);
            }
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedProduct(null);
    };

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <InventoryIcon fontSize="large" />
                    Inventory Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setModalOpen(true)}
                    sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d47a1' } }}
                >
                    Add Product
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3, borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <TextField
                    fullWidth
                    placeholder="Search products by name, SKU, or category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            bgcolor: '#f5f5f5',
                            '& fieldset': { border: 'none' },
                        }
                    }}
                />
            </Paper>

            <Paper sx={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                {loading ? (
                    <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Price</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Stock</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredProducts.map((product) => (
                                <TableRow key={product.id} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{product.name}</TableCell>
                                    <TableCell>{product.sku}</TableCell>
                                    <TableCell>{product.category || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={product.type}
                                            size="small"
                                            color={product.type === 'GOODS' ? 'primary' : 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">${Number(product.price).toFixed(2)}</TableCell>
                                    <TableCell align="right">
                                        {product.type === 'GOODS' ? product.stockQuantity : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {product.type === 'GOODS' && product.stockQuantity <= product.minQuantity && (
                                            <Chip
                                                icon={<WarningIcon />}
                                                label="Low Stock"
                                                color="warning"
                                                size="small"
                                            />
                                        )}
                                        {product.type === 'GOODS' && product.stockQuantity > product.minQuantity && (
                                            <Chip label="In Stock" color="success" size="small" variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <IconButton onClick={() => handleEdit(product)} color="primary" size="small">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton onClick={() => handleDelete(product.id)} color="error" size="small">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            <AddProductModal
                open={modalOpen}
                onClose={handleModalClose}
                onSuccess={fetchProducts}
                productToEdit={selectedProduct}
            />
        </Box>
    );
};

export default InventoryPage;
